// @flow
import * as React from 'react'
import { FormattedMessage } from 'mz-intl'
import omitEmptyFields from 'mz-utils/omitEmptyFields'
import cloneDatetime from 'mz-utils/cloneDatetime'
import cloneDatetimeWithoutTimezone from 'mz-utils/cloneDatetimeWithoutTimezone'
import type { Flight } from 'mz-sdk/services/flights/getFlights'
import getFlights from 'mz-sdk/services/flights/getFlights'
import { isMobileBrowser } from '../../../utils'
import Button from '../../../core/Button'
import { Row } from '../../../core/Row'
import Form from '../../Form'
import DateField from '../../DateField/field'
import TimeField from '../../TimeField/field'
import Lightbox from '../../../core/Lightbox'
import FlightField from '../BasicFlightField/field'
import AirlineField from '../../AirlineField/field'
import AirportField from '../../AirportField/field'
import FlightSearchResult from '../FlightSearchResult'
import {
  Container,
  Content,
  ButtonContainer,
  AlertContent,
  IconWrapper,
  AlertMessageContainer,
  AlertContainer,
} from './elements'
import messages from '../messages.intl'
import InfoIcon from 'mz-icons/info-circle'
import Alert from 'mz-ui-kit/core/Alert'

// Types
import type { FlightInfo } from '../BasicFlightField'
import type { Appearance as FieldAppearance } from '../../../core/BaseField'

export type Appearance = 'desktop' | 'mobile'
export type Props = {
  appearance?: FieldAppearance,
  flightInfo: FlightInfo,
  onSelectFlight?: (Flight) => any,
  controllerRef: (*) => *,
  finderAppearance?: Appearance,
  onClose?: () => any,
}
type State = {
  airlineCode: string,
  airlineName: string,
  searchFinished: boolean,
  searchFlightDatetime: string,
  exactTimeFlights: Flight[],
  closeTimeFlights: Flight[],
}

// Utils
const INITIAL_STATE = {
  airlineCode: '',
  airlineName: '',
  searchFinished: false,
  closeTimeFlights: [],
  exactTimeFlights: [],
  searchFlightDatetime: '',
}

function mergeDateAndTime(date, time, baseDate) {
  const actualBaseDate = cloneDatetime(baseDate || date)
  return actualBaseDate
    .startOf('minute')
    .year(date.year())
    .month(date.month())
    .date(date.date())
    .hours(time.hours())
    .minutes(time.minutes())
}

/**
 * For flight datetime prefill both date and time fields.
 * For pickup datetime prefill only date field
 * @param  {FlightInfo} flightInfo
 * @return {Object}
 */
function getInitDateFromFlightInfo(flightInfo) {
  const { flightDatetime, pickupDatetime } = flightInfo
  if (flightDatetime) {
    const parsedDatetime = cloneDatetimeWithoutTimezone(flightDatetime)
    return {
      date: parsedDatetime.clone(),
      time: parsedDatetime.clone(),
    }
  }
  if (pickupDatetime) {
    const parsedDatetime = cloneDatetimeWithoutTimezone(pickupDatetime)
    return { date: parsedDatetime }
  }
  return {}
}

/**
 * Wrapper around FlightField which adds a lightbox with
 * extra fields to find a flight. It manages all the state
 * internally.
 */
export class FlightFinderField extends React.Component<Props, State> {
  static defaultProps = FlightField.defaultProps

  state = INITIAL_STATE

  lightboxController = null

  handleSelectFlight = (flight: Flight) => {
    const { onSelectFlight, onClose } = this.props
    onSelectFlight && onSelectFlight(flight)
    onClose && onClose()
  }

  handleSubmitFinder = async (vals: Object) => {
    const {
      airportCode,
      flightDirection,
      flightDatetime,
      pickupDatetime,
    } = this.props.flightInfo

    const baseDatetime = flightDatetime || pickupDatetime
    const selectedAirportCode = vals.airport && vals.airport.value
    const searchFlightDatetime = mergeDateAndTime(
      vals.date,
      vals.time,
      baseDatetime
    )
    const compareFlightDatetime = cloneDatetimeWithoutTimezone(
      searchFlightDatetime
    )
    const searchRange = [
      compareFlightDatetime.clone().add(-30, 'minutes'),
      compareFlightDatetime.clone().add(30, 'minutes'),
    ]

    const flights = await getFlights(
      omitEmptyFields({
        airport_code: airportCode,
        other_airport_code: selectedAirportCode,
        flight_direction: flightDirection,
        airline_code: vals.airline.value,
        flight_datetime: searchFlightDatetime.format(),
      })
    )

    const exactTimeFlights = []
    const closeTimeFlights = []
    flights.forEach((flight) => {
      const flightMoment = cloneDatetimeWithoutTimezone(flight.flightDatetime)
      const otherAirport =
        flightDirection === 'arr' ? flight.origin : flight.destination
      const matchAirport =
        !selectedAirportCode || selectedAirportCode === otherAirport

      if (flightMoment.isSame(compareFlightDatetime) && matchAirport) {
        exactTimeFlights.push(flight)
      } else if (
        (selectedAirportCode && matchAirport) ||
        (!selectedAirportCode && flightMoment.isBetween(...searchRange))
      ) {
        closeTimeFlights.push(flight)
      }
    })

    this.setState({
      airlineCode: vals.airline.value,
      airlineName: vals.airline.display,
      searchFinished: true,
      searchFlightDatetime: searchFlightDatetime.format(),
      exactTimeFlights,
      closeTimeFlights,
    })
  }

  setLightboxController = (c: *) => {
    this.props.controllerRef && this.props.controllerRef(c)
    this.lightboxController = c
  }

  renderSearchResults = () =>
    this.state.searchFinished && (
      <FlightSearchResult
        airlineName={this.state.airlineName}
        airlineCode={this.state.airlineCode}
        flightInfo={this.props.flightInfo}
        searchFlightDatetime={this.state.searchFlightDatetime}
        exactTimeFlights={this.state.exactTimeFlights}
        closeTimeFlights={this.state.closeTimeFlights}
        onSelectFlight={this.handleSelectFlight}
      />
    )

  renderFinderForm = ({ submit, meta }: *, nest: *) => {
    const { flightDirection, airportCode } = this.props.flightInfo
    const flightTimeLabel =
      flightDirection === 'arr'
        ? messages.FLIGHT_TIME_LABEL_LANDING
        : messages.FLIGHT_TIME_LABEL_DEPARTING
    const airportLabel =
      flightDirection === 'arr'
        ? messages.ORIGIN_AIRPORT
        : messages.DESTINATION_AIRPORT

    return (
      <div>
        <Row wrap="xs">
          <DateField
            {...nest('date')}
            required
            appearance={this.props.appearance}
            fieldAppearance={this.props.finderAppearance}
            label={<FormattedMessage message={messages.FLIGHT_DATE_LABEL} />}
            placeholder={
              <FormattedMessage message={messages.FLIGHT_DATE_PLACEHOLDER} />
            }
          />
          <TimeField
            {...nest('time')}
            required
            appearance={this.props.appearance}
            roundMinutes={false}
            fieldAppearance={this.props.finderAppearance}
            label={
              <FormattedMessage
                message={flightTimeLabel}
                values={{ airport: airportCode }}
              />
            }
            placeholder={
              <FormattedMessage message={messages.FLIGHT_TIME_PLACEHOLDER} />
            }
          />
        </Row>
        <Row wrap="xs">
          <AirlineField
            {...nest('airline')}
            required
            appearance={this.props.appearance}
            fieldAppearance={this.props.finderAppearance}
            label={<FormattedMessage message={messages.YOUR_AIRLINE_LABEL} />}
            placeholder={
              <FormattedMessage message={messages.YOUR_AIRLINE_PLACEHOLDER} />
            }
          />
          <AirportField
            {...nest('airport')}
            appearance={this.props.appearance}
            fieldAppearance={this.props.finderAppearance}
            label={<FormattedMessage message={airportLabel} />}
          />
        </Row>
        <AlertContainer>
          <Alert color="yellow">
            <AlertContent>
              <IconWrapper>
                <InfoIcon />
              </IconWrapper>
              <FormattedMessage
                tagName={AlertMessageContainer}
                message={messages.LAYOVER_EXPLANATION}
                values={{ airport: airportCode }}
              />
            </AlertContent>
          </Alert>
        </AlertContainer>
        <ButtonContainer>
          <Button onClick={submit} loading={meta.deepLoading}>
            <FormattedMessage message={messages.SELECT_FLIGHT} />
          </Button>
        </ButtonContainer>
      </div>
    )
  }

  render() {
    const styles = { $appearance: this.props.finderAppearance }
    const initValue = getInitDateFromFlightInfo(this.props.flightInfo)

    return (
      <Container {...styles}>
        <Content {...styles}>
          <Form
            render={this.renderFinderForm}
            onSubmit={this.handleSubmitFinder}
            initValue={initValue}
          />
          {this.renderSearchResults()}
        </Content>
      </Container>
    )
  }
}

/**
 * Tiny wrapper around Lightbox to show flight finder tool with a propper
 * appearance and title
 * @param {Props} props
 */
export const FlightFinderLightbox = (props: Props) => {
  const finderAppearance = !props.finderAppearance
    ? isMobileBrowser()
      ? 'mobile'
      : 'desktop'
    : props.finderAppearance

  return (
    <Lightbox
      controllerRef={props.controllerRef}
      title={<FormattedMessage message={messages.LIGHTBOX_TITLE} />}
      appearance={finderAppearance}
      render={({ close }) => (
        <FlightFinderField
          {...props}
          finderAppearance={finderAppearance}
          onClose={close}
        />
      )}
    />
  )
}

export default FlightFinderLightbox
